"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useId, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { Dictionary } from "@/dictionaries/types";
import { Segmented } from "@/features/home-v2/ui";
type Point = {
  date: string;
  value: number;
};
const METRICS = ["volume", "sales", "average_price"] as const;
type Metric = (typeof METRICS)[number];
/** One colour per metric, from the system: a series keeps its identity across
 *  a theme switch because the token does. */
const SERIES: Record<Metric, string> = {
  volume: "var(--v2-accent)",
  sales: "var(--v2-gold)",
  average_price: "var(--v2-info)"
};
type Props = {
  lang: "fa" | "en";
  charts: Record<Metric, Point[]>;
  currency: string;
  t: Dictionary["collectionPage"];
};

/**
 * THE SALES CHART — the legacy chart's contract, kept: the endpoint returns
 * points newest-first, so they are sorted oldest → newest once for both
 * locales (a time axis is not mirrored); the plot is keyboard-walkable
 * (`accessibilityLayer`); and the same numbers are one disclosure away as a
 * real table. `sales` is a count and carries no ticker; the amounts carry
 * the chain's.
 */
export default function AnalyticsChart(t0) {
  const $ = _c(39);
  const {
    lang,
    charts,
    currency,
    t
  } = t0;
  const isFa = lang === "fa";
  const locale = isFa ? "fa-IR" : "en-US";
  const tableId = useId();
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /:/g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(t3, "");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const gradient = t2;
  const [metric, setMetric] = useState("volume");
  const [tableOpen, setTableOpen] = useState(false);
  let t3;
  if ($[3] !== charts || $[4] !== currency || $[5] !== gradient || $[6] !== isFa || $[7] !== locale || $[8] !== metric || $[9] !== t.analyticsChartDescription || $[10] !== t.analyticsChartTitle || $[11] !== t.chartColumnDate || $[12] !== t.chartColumnValue || $[13] !== t.chartEmpty || $[14] !== t.chartMetricLabel || $[15] !== t.chartTableLabel || $[16] !== t.chartTableToggleHide || $[17] !== t.chartTableToggleShow || $[18] !== t.metricAveragePrice || $[19] !== t.metricSales || $[20] !== t.metricVolume || $[21] !== tableId || $[22] !== tableOpen) {
    const names = {
      volume: t.metricVolume,
      sales: t.metricSales,
      average_price: t.metricAveragePrice
    };
    const isCount = metric === "sales";
    let t4;
    if ($[24] !== charts || $[25] !== metric) {
      t4 = (charts[metric] ?? []).map(_temp).sort(_temp2);
      $[24] = charts;
      $[25] = metric;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    const data = t4;
    let t5;
    if ($[27] !== locale) {
      t5 = s => {
        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(locale, {
          month: "short",
          day: "numeric"
        });
      };
      $[27] = locale;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    const date = t5;
    let t6;
    if ($[29] !== isCount || $[30] !== locale) {
      t6 = v => isCount ? Math.round(v).toLocaleString(locale) : v.toLocaleString(locale, {
        maximumFractionDigits: 4
      });
      $[29] = isCount;
      $[30] = locale;
      $[31] = t6;
    } else {
      t6 = $[31];
    }
    const value = t6;
    let t7;
    if ($[32] !== currency || $[33] !== isCount || $[34] !== value) {
      t7 = v_0 => isCount ? value(v_0) : `${value(v_0)} ${currency}`;
      $[32] = currency;
      $[33] = isCount;
      $[34] = value;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    const amount = t7;
    const description = t.analyticsChartDescription.replace("{metric}", names[metric]);
    const t8 = `${tableId}-h`;
    let t9;
    if ($[36] !== t.analyticsChartTitle || $[37] !== t8) {
      t9 = <h3 id={t8}>{t.analyticsChartTitle}</h3>;
      $[36] = t.analyticsChartTitle;
      $[37] = t8;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    t3 = <section className="v2-cd-chart" aria-labelledby={`${tableId}-h`}><header className="v2-cd-chart-head"><div>{t9}<p>{description}</p></div><Segmented value={metric} onChange={setMetric} label={t.chartMetricLabel} options={METRICS.map(m => ({
          value: m,
          label: names[m]
        }))} /></header>{data.length === 0 ? <p className="v2-cd-chart-empty">{t.chartEmpty}</p> : <><div className="v2-cd-chart-plot" role="img" aria-label={`${t.analyticsChartTitle} — ${description}`}><ResponsiveContainer width="100%" height={260}><AreaChart data={data} margin={{
              left: 8,
              right: 8,
              top: 10,
              bottom: 0
            }} accessibilityLayer={true}><defs><linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={SERIES[metric]} stopOpacity={0.28} /><stop offset="100%" stopColor={SERIES[metric]} stopOpacity={0} /></linearGradient></defs><CartesianGrid vertical={false} strokeDasharray="3 4" stroke="var(--v2-line)" /><XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={40} tickFormatter={date} tick={{
                fontSize: 11,
                fill: "var(--v2-muted)"
              }} /><YAxis tickLine={false} axisLine={false} tickMargin={8} width={isCount ? isFa ? 44 : 36 : isFa ? 68 : 56} tickFormatter={v_1 => value(Number(v_1))} tick={{
                fontSize: 11,
                fill: "var(--v2-muted)"
              }} /><Tooltip cursor={{
                stroke: "var(--v2-line-2)"
              }} content={t10 => {
                const {
                  active,
                  payload,
                  label
                } = t10;
                return active && payload?.length ? <div className="v2-cd-chart-tip"><span>{date(String(label))}</span><b>{amount(Number(payload[0].value))}</b></div> : null;
              }} /><Area dataKey="value" type="monotone" stroke={SERIES[metric]} strokeWidth={2.4} fill={`url(#${gradient})`} connectNulls={true} dot={data.length <= 7 ? {
                r: 3,
                fill: SERIES[metric],
                strokeWidth: 0
              } : false} activeDot={{
                r: 5
              }} /></AreaChart></ResponsiveContainer></div><button type="button" className="v2-cd-chart-toggle v2-focus" aria-expanded={tableOpen} aria-controls={tableId} onClick={() => setTableOpen(_temp3)}>{tableOpen ? t.chartTableToggleHide : t.chartTableToggleShow}</button>{tableOpen ? <div id={tableId} className="v2-cd-table v2-cd-chart-table"><table><caption className="v2-sr-only">{t.chartTableLabel.replace("{metric}", names[metric])}</caption><thead><tr><th scope="col">{t.chartColumnDate}</th><th scope="col" className="v2-cd-c-end">{t.chartColumnValue}</th></tr></thead><tbody>{data.map(p_0 => <tr key={p_0.date}><th scope="row">{date(p_0.date)}</th><td className="v2-cd-c-end">{amount(p_0.value)}</td></tr>)}</tbody></table></div> : null}</>}</section>;
    $[3] = charts;
    $[4] = currency;
    $[5] = gradient;
    $[6] = isFa;
    $[7] = locale;
    $[8] = metric;
    $[9] = t.analyticsChartDescription;
    $[10] = t.analyticsChartTitle;
    $[11] = t.chartColumnDate;
    $[12] = t.chartColumnValue;
    $[13] = t.chartEmpty;
    $[14] = t.chartMetricLabel;
    $[15] = t.chartTableLabel;
    $[16] = t.chartTableToggleHide;
    $[17] = t.chartTableToggleShow;
    $[18] = t.metricAveragePrice;
    $[19] = t.metricSales;
    $[20] = t.metricVolume;
    $[21] = tableId;
    $[22] = tableOpen;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  return t3;
}
function _temp3(v_2) {
  return !v_2;
}
function _temp2(a, b) {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}
function _temp(p) {
  return {
    date: p.date,
    value: Number(p.value)
  };
}